import { ChatMessage } from '../../types/ai.types';
import { Volume2, Square, Loader2, Copy, Check } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { TextNormalizer } from '../../utils/textNormalizer';
import { geminiService } from '../../services/gemini.service';

import { registerGlobalAudio } from '../../utils/speechQueueManager';

interface ChatBubbleProps {
  message: ChatMessage;
  isLastMessage?: boolean;
  globalAudioState?: {
    isPlaying: boolean;
    toggle: () => void;
  };
}

export function ChatBubble({ message, isLastMessage, globalAudioState }: ChatBubbleProps) {
  const isUser = message.sender === 'user';
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [copied, setCopied] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const playAudio = async (textToSpeak: string) => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.warn(e));
      return;
    }
    
    setIsLoadingAudio(true);
    try {
      const url = await geminiService.generateSpeech(textToSpeak);
      const audio = new Audio(url);
      registerGlobalAudio(audio);
      audioRef.current = audio;
      
      audio.onplay = () => setIsPlaying(true);
      audio.onended = () => setIsPlaying(false);
      audio.onpause = () => setIsPlaying(false);
      
      audio.play().catch(e => {
        console.warn("Otomatik oynatma engellendi:", e);
        setIsPlaying(false);
      });
    } catch (e) {
      console.error("Ses üretimi başarısız:", e);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const toggleSpeak = () => {
    if (isLastMessage && globalAudioState) {
      globalAudioState.toggle();
      return;
    }

    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      return;
    }

    const textToSpeak = TextNormalizer.normalize(message.text);
    if (!textToSpeak) return;

    playAudio(textToSpeak);
  };

  useEffect(() => {
    const handleAudioStopped = () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      setIsPlaying(false);
    };

    window.addEventListener('app:audio-stopped', handleAudioStopped);

    return () => {
      window.removeEventListener('app:audio-stopped', handleAudioStopped);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.id, isUser, message.isError]);

  return (
    <div className={`ai-mentor-message ${isUser ? 'user' : 'ai'}`}>
      <div className="ai-mentor-avatar" aria-hidden="true">
        {isUser ? '🧑' : '🤖'}
      </div>
      <div
        className={`ai-mentor-bubble ${isUser ? 'user' : 'ai'} ${message.isError ? 'error' : ''}`}
        role={message.isError ? 'alert' : undefined}
      >
        {isUser ? (
          <div style={{ whiteSpace: 'pre-wrap' }}>{message.text}</div>
        ) : (
          <div className="ai-mentor-markdown-content">
            <ReactMarkdown>{message.text}</ReactMarkdown>
          </div>
        )}

        {!isUser && !message.isError && (
          <div className="ai-mentor-bubble-actions">
            <button 
              type="button"
              className="ai-mentor-action-btn" 
              onClick={toggleSpeak}
              disabled={isLoadingAudio}
              title={isLoadingAudio ? "Ses yükleniyor..." : "Seslendir / Duraklat"}
            >
              {isLoadingAudio ? <Loader2 size={13} className="animate-spin" /> : 
                (isLastMessage && globalAudioState 
                  ? (globalAudioState.isPlaying ? <Square size={13} /> : <Volume2 size={13} />)
                  : (isPlaying ? <Square size={13} /> : <Volume2 size={13} />)
                )
              }
              <span>
                {isLoadingAudio ? 'Yükleniyor...' : 
                  (isLastMessage && globalAudioState
                    ? (globalAudioState.isPlaying ? 'Duraklat' : 'Devam Et')
                    : (isPlaying ? 'Durdur' : 'Seslendir')
                  )
                }
              </span>
            </button>
            <button
              type="button"
              className="ai-mentor-action-btn"
              onClick={handleCopy}
              title="Metni Kopyala"
            >
              {copied ? <Check size={13} style={{ color: '#10B981' }} /> : <Copy size={13} />}
              <span>{copied ? 'Kopyalandı' : 'Kopyala'}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
